if (!customElements.get('promotion-container')) {
  customElements.define(
    'promotion-container',
    class PromotionContainer extends HTMLElement {
      constructor() {
        super();
        this.debug = false;
        this.cartDrawer =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-modal');
        this.cartPage = document.querySelector('cart-items');
        this.byPassSliderCart = document.querySelector('[data-bypass-slider-cart]') || false;
        this.CHARSET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
        this.type = this.getAttribute('data-type');
        this.minSpend = this.hasAttribute('data-min-spend') ? this.getAttribute('data-min-spend') : 0;
        this.promotionJson = this.querySelector('script').innerHTML;
        this.promotion = JSON.parse(this.promotionJson || '[]').sort((a, b) => a.v - b.v);
        this.qualifyingCode = this.getAttribute('data-qualifying-code');
        this.initialConfig = this.getAttribute('data-initial-config') == 'true';
        this.triggerVariants = new Set(
          (this.getAttribute('data-trigger-variants') || '')
            .split(',')
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => !isNaN(n))
        );
        // Member Reward promo: many independent trigger-SKU -> gift-variant groups.
        // Each group is reconciled separately so a cart can qualify for several
        // gift-card denominations at once. Trigger matching is done by cart-line
        // SKU (from /cart.js), so no trigger variant-id list is needed.
        this.memberRewardGroups =
          this.type === 'member_reward'
            ? this.promotion
                .filter((group) => group && group.g)
                .map((group) => ({
                  giftVariantId: group.g,
                  available: group.y !== false,
                  triggerSkus: new Set(
                    String(group.s || '')
                      .split(',')
                      .map((sku) => sku.trim().toUpperCase())
                      .filter(Boolean)
                  ),
                }))
                .filter((group) => group.available && group.triggerSkus.size > 0)
            : [];
        // Member Reward can be gated to logged-in customers. Login state is
        // resolved server-side per request and passed in via data attributes.
        // When gated for a guest, the promo adds nothing and any lingering gift
        // card is reconciled out on the next cart update.
        this.requiresLogin = this.getAttribute('data-requires-login') === 'true';
        this.customerLoggedIn = this.getAttribute('data-customer-logged-in') === 'true';
        this.memberRewardBlocked = this.requiresLogin && !this.customerLoggedIn;
        // When enforced, the reward gift is treated as mandatory: reconciled on
        // every page load (not just on cart events), and never suppressed by the
        // manual-dismissal flag — the cart UI hides the gift's remove control, so
        // a missing gift means the promo logic simply has not run yet, not that
        // the customer opted out.
        this.enforceGift = this.getAttribute('data-enforce-gift') === 'true';
        this._reconciling = false;
        // Serializes every member-reward reconcile onto one promise chain so the
        // multiple entry points (connect, cartUpdate, product-form add) can never
        // read a stale pre-gift snapshot and each add the gift. See
        // queueMemberRewardReconcile.
        this._memberRewardChain = null;
        // Set true when a queued reconcile mutates the cart, so handlePromotion
        // can report back to product-form that the promotion already re-rendered
        // the cart (and product-form must not overwrite it with a pre-gift state).
        this._memberRewardRendered = false;
      }

      /**
       * Lifecycle method called when element is inserted into the DOM.
       * Subscribes to cart update events to monitor cart changes in real-time.
       *
       * For a login-gated Member Reward promo, also runs a one-time reconcile
       * on connect. Standard login always ends in a full page reload (the
       * Amplify -> Shopify Multipass bridge finishes with `window.location.href`),
       * so a guest who already had a qualifying trigger product in the cart and
       * then logs in lands on a freshly rendered page as a logged-in customer.
       * Gift reconciliation is otherwise driven only by `cartUpdate` events, so
       * nothing would grant the now-earned gift until the shopper next touched
       * the cart. Reconciling on connect closes that gap. The reconcile is
       * idempotent — it respects the per-group manual-dismissal flag and any
       * gift line already present — so a shopper who was logged in the whole
       * time (gift already in cart) is a no-op.
       */
      connectedCallback() {
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));

        if (this.type === 'member_reward' && this.memberRewardGroups.length) {
          // Reconcile on connect so gift state is correct after login, refresh, or
          // navigation (all of which fire connectedCallback) — not only after a
          // cartUpdate. Quiet: no user interaction has occurred, so nothing opens
          // the cart drawer/notification or traps focus. The cartUpdate-driven
          // path stays non-quiet so a user-initiated add still surfaces the cart.
          //
          // `removeOnly` splits the two on-connect responsibilities:
          //   - Enforced gift, or login-gated + logged-in → removeOnly = false:
          //     the full add + remove reconcile, byte-for-byte the pre-existing
          //     "add the gift back on load / after login" flow.
          //   - Everything else (non-gated/non-enforced promo, or a login-gated
          //     guest) → removeOnly = true: a self-heal that ONLY performs scoped
          //     removals, cleaning an orphaned gift whose trigger has left without
          //     ever introducing a new silent add. Both are idempotent and respect
          //     the per-group manual-dismissal flag.
          const removeOnly = !(this.enforceGift || (this.requiresLogin && this.customerLoggedIn));
          this.handleMemberReward(true, removeOnly);
        }
      }

      /**
       * Entry point for promotion handling.
       * Routes to the appropriate handler based on promotion type.
       *
       * @returns {Promise<boolean>} True if promotion was handled successfully, false otherwise
       */
      async handlePromotion() {
        if (!this.promotion.length || !this.type) return false;

        switch (this.type) {
          case 'discountcode_gwp':
            return await this.handleDiscountcodeGWP();
          case 'min_spend':
            return await this.handleMinSpend();
          case 'bxgy':
            return await this.handleBXGY();
          case 'member_reward':
            // Await the serialized reconcile (which also drains the cartUpdate
            // reconcile product-form triggered via publish), then report whether
            // the promotion rendered so product-form doesn't overwrite the gift
            // with the pre-gift add response.
            this._memberRewardRendered = false;
            await this.handleMemberReward();
            return this._memberRewardRendered;
          default:
            return false;
        }
      }

      /**
       * Handles minimum spend promotion logic.
       * Checks if cart total (after discounts) meets minimum spend threshold
       * and adds gift item if qualified.
       *
       * @returns {Promise<boolean>} True if gift was added successfully, false otherwise
       */
      async handleMinSpend() {
        try {
          const cart = await fetch('/cart.js');
          const { items, total_price } = await cart.json();

          if (!items.some((item) => item.properties['_free-gift']) && total_price / 100 >= this.minSpend) {
            const promoItem = this.promotion.find((promoItem) => promoItem.x);
            return this.addItem({ item: this.promotion[0].x, discount: null });
          }
        } catch (e) {
          console.error(e);
        }
        return false;
      }

      /**
       * Handles Buy X Get Y promotion logic.
       * Fetches current cart and reconciles the gift line so that its quantity
       * matches the total quantity of qualifying trigger variants in the cart.
       *
       * @returns {Promise<boolean>} True if any cart change was made, false otherwise
       */
      async handleBXGY() {
        try {
          const cart = await fetch('/cart.js');
          const cartData = await cart.json();
          return await this.reconcileBXGY(cartData);
        } catch (e) {
          console.error(e);
        }
        return false;
      }

      /**
       * Reconciles the BXGY gift line against current cart state.
       * Sums quantities of trigger variants and ensures the gift line matches:
       * adds it if missing, updates qty if mismatched, removes it if no triggers remain.
       * Re-entrancy guarded so concurrent cart updates don't double-add.
       *
       * @param {Object} cartData - Cart state with `items` array
       * @returns {Promise<boolean>} True if a cart mutation was issued
       */
      async reconcileBXGY(cartData) {
        if (this._reconciling) return false;
        const giftVariantId = this.promotion[0]?.x;
        if (!giftVariantId) return false;
        const items = cartData.items || [];
        const itemsRemoved = cartData.items_removed || [];

        let desiredQty = 0;
        for (const item of items) {
          if (item.properties && item.properties['_free-gift']) continue;
          if (this.triggerVariants.has(item.variant_id)) {
            desiredQty += item.quantity;
          }
        }

        const giftLineIndex = items.findIndex(
          (item) =>
            item.properties && item.properties['_free-gift'] && item.variant_id === giftVariantId
        );
        const giftLine = giftLineIndex >= 0 ? items[giftLineIndex] : null;

        // If the gift just left the cart while triggers are still present,
        // the customer removed it manually. Honor that for the rest of the
        // session — don't auto-re-add until triggers fully leave and return.
        // Note: Shopify's `items_removed` entries may not preserve line
        // `properties`, so match on variant_id alone. The giftLine lookup
        // above already enforces the `_free-gift` property, so a missing
        // giftLine combined with `variant_id` in items_removed is a reliable
        // signal of customer-initiated removal of our gift line.
        if (desiredQty > 0 && !giftLine) {
          const userRemovedGift = itemsRemoved.some(
            (i) => i && i.variant_id === giftVariantId
          );
          if (userRemovedGift) {
            try {
              sessionStorage.setItem('bxgy_dismissed', '1');
            } catch (e) {
              // sessionStorage can throw in private mode; safe to ignore
            }
            if (this.debug) console.log('bxgy: customer removed gift, dismissing for session');
          }
        }

        const dismissed = (() => {
          try {
            return sessionStorage.getItem('bxgy_dismissed') === '1';
          } catch (e) {
            return false;
          }
        })();

        this._reconciling = true;
        try {
          if (desiredQty === 0) {
            // Cart no longer qualifies — clear any dismissal so the next
            // trigger-add cycle starts fresh.
            try {
              sessionStorage.removeItem('bxgy_dismissed');
            } catch (e) {}
            if (giftLine) {
              if (this.debug) console.log('bxgy: removing gift, no triggers left');
              await this.removeItem(giftLine.key, giftLineIndex);
              return true;
            }
            return false;
          }

          if (dismissed) {
            if (this.debug) console.log('bxgy: dismissed, leaving cart alone');
            return false;
          }

          if (!giftLine) {
            if (this.debug) console.log('bxgy: adding gift x', desiredQty);
            const result = await this.addItem({ item: giftVariantId, quantity: desiredQty });
            return !!result?.success;
          }

          if (giftLine.quantity !== desiredQty) {
            if (this.debug) console.log('bxgy: updating gift qty', giftLine.quantity, '->', desiredQty);
            return await this.updateItemQty(giftLine.key, desiredQty);
          }

          return false;
        } finally {
          this._reconciling = false;
        }
      }

      /**
       * Handles Member Reward promotion logic.
       * Fetches the current cart and reconciles every gift-card denomination
       * group against the qualifying trigger SKUs in the cart.
       *
       * @param {boolean} [quiet] - When true, a gift added by this reconcile
       *   renders the cart silently (no drawer/notification open, no focus move).
       *   Used for the on-connect reconcile after login. Default false.
       * @param {boolean} [removeOnly] - When true, the reconcile skips the add and
       *   quantity-update branches and performs only scoped gift removals. Used by
       *   the connect-time self-heal so an orphaned gift is cleaned without ever
       *   introducing a new silent add. Default false.
       * @returns {Promise<boolean>} True if any cart change was made, false otherwise
       */
      async handleMemberReward(quiet = false, removeOnly = false) {
        return this.queueMemberRewardReconcile(quiet, null, removeOnly);
      }

      /**
       * Serializes every member-reward reconcile — the on-connect load reconcile,
       * the cartUpdate-driven reconcile, and the product-form add hook all funnel
       * through one promise chain. Each queued run reads a FRESH /cart.js snapshot
       * inside its own turn (see runMemberRewardReconcile), so it observes any gift
       * a prior run just added. This closes the check-then-act window in which two
       * entry points fired on a single add-to-cart, each read a pre-gift snapshot,
       * and each added the gift — producing a duplicate gift-card line.
       *
       * @param {boolean} [quiet] - Forwarded to the reconcile.
       * @param {Array} [itemsRemoved] - The triggering cartUpdate event's
       *   `items_removed`, if any. A fresh GET /cart.js (read inside the turn)
       *   does not carry this, so the manual-removal / dismissal logic depends on
       *   it being threaded through from the event payload.
       * @param {boolean} [removeOnly] - Forwarded to the reconcile; when true it
       *   performs only scoped removals (no add / no quantity update).
       * @returns {Promise<boolean>} Resolves once this run finishes; true if it
       *   mutated the cart.
       */
      queueMemberRewardReconcile(quiet = false, itemsRemoved = null, removeOnly = false) {
        const run = () => this.runMemberRewardReconcile(quiet, itemsRemoved, removeOnly);
        this._memberRewardChain = Promise.resolve(this._memberRewardChain)
          .catch(() => {})
          .then(run);
        return this._memberRewardChain;
      }

      /**
       * One serialized reconcile turn: read the current cart, then reconcile the
       * gift lines against it. Always fetches fresh so it sees mutations from the
       * previous turn in the chain.
       *
       * @param {boolean} quiet - Forwarded to reconcileMemberReward.
       * @param {Array} [itemsRemoved] - The triggering event's `items_removed`.
       *   Merged onto the freshly-fetched cart so manual-removal detection works
       *   (a GET /cart.js omits this field). The `!giftLine` guard in
       *   reconcileMemberReward means a stale value can't cause a wrong dismissal.
       * @param {boolean} [removeOnly] - Forwarded to reconcileMemberReward; when
       *   true only scoped gift removals run (no add / no quantity update).
       * @returns {Promise<boolean>} True if the cart was mutated.
       */
      async runMemberRewardReconcile(quiet, itemsRemoved = null, removeOnly = false) {
        try {
          const cart = await fetch('/cart.js');
          const cartData = await cart.json();
          if (itemsRemoved) cartData.items_removed = itemsRemoved;
          const mutated = await this.reconcileMemberReward(cartData, quiet, removeOnly);
          if (mutated) this._memberRewardRendered = true;
          return mutated;
        } catch (e) {
          console.error(e);
        }
        return false;
      }

      /**
       * Reconciles Member Reward gift lines against current cart state by COUNT,
       * aggregated per gift variant. Phase 1 sums, across every group, the desired
       * gift count for each gift variant (Σ trigger-line quantities; 0 when the
       * shopper is a gated-out guest). Phase 2 gathers every `_free-gift` cart line
       * for that variant, sums their quantities into `currentCount`, and adds when
       * short, removes/decrements the excess when over — undiscounted lines first.
       *
       * Counting units (not lines) is essential: Shopify never merges gift-card
       * lines (each mints a unique code), so a single gift variant routinely holds
       * several separate qty-1 lines. The prior single-line model could not see —
       * let alone reduce — that population, which is what orphaned undiscounted
       * duplicates. Re-entrancy guarded so concurrent cart updates don't double-add.
       *
       * @param {Object} cartData - Cart state with `items` array
       * @param {boolean} [quiet] - When true, a newly added gift renders the
       *   cart silently (no drawer/notification open, no focus move). Removals
       *   and quantity updates are always silent. Default false.
       * @param {boolean} [removeOnly] - When true, the add branch is skipped and
       *   only excess removals run (bringing each variant's count down to, never
       *   up to, its desired count). Used by the connect-time self-heal to clean
       *   orphaned/excess gifts without ever adding one. Default false — full
       *   add + remove reconcile.
       * @returns {Promise<boolean>} True if a cart mutation was issued
       */
      async reconcileMemberReward(cartData, quiet = false, removeOnly = false) {
        if (this._reconciling) return false;
        const groups = this.memberRewardGroups;
        if (!groups || !groups.length) return false;

        const items = cartData.items || [];
        const itemsRemoved = cartData.items_removed || [];

        this._reconciling = true;
        let mutated = false;
        // Keep checkout disabled for one continuous span across the whole
        // reconcile pass (which may issue several sequential gift-line mutations)
        // rather than letting each removeItem/updateItemQty toggle it per line —
        // hence they are called with manageSubmitButtons = false below. On a
        // no-op reconcile the disable/re-enable happen synchronously with no
        // await between, so nothing paints.
        const submitButtons = document.querySelectorAll('.cart__ctas button[type="submit"]');
        submitButtons.forEach((button) => button.setAttribute('disabled', true));
        try {
          // Phase 1 — desired gift *count* per gift variant, summed across every
          // group. Shopify never merges gift-card lines (each mints a unique
          // code), so one gift variant can legitimately occupy several separate
          // qty-1 lines. Counting units — not lines — is the only model that can
          // represent, and therefore reduce, a multi-line gift population.
          // Config is 1 group : 1 variant today, but summing future-proofs a
          // shared-variant config. A variant that no longer qualifies is still
          // recorded with a desired count of 0 so its stale lines get removed in
          // phase 2.
          const desiredByVariant = new Map();
          for (const group of groups) {
            // When gated to logged-in customers and the shopper is a guest,
            // leave desiredQty at 0: no gift is added, and phase 2 clears any
            // gift card already sitting in the cart.
            let desiredQty = 0;
            if (!this.memberRewardBlocked) {
              for (const item of items) {
                if (item.properties && item.properties['_free-gift']) continue;
                const sku = (item.sku || '').toUpperCase();
                if (sku && group.triggerSkus.has(sku)) {
                  desiredQty += item.quantity;
                }
              }
            }
            const previousDesired = desiredByVariant.get(group.giftVariantId) || 0;
            desiredByVariant.set(group.giftVariantId, previousDesired + desiredQty);
          }

          // Undiscounted (un-entitled) gift lines rank first for removal so the
          // entitled/discounted line survives and UI flicker is minimized.
          // `total_discount` is the most reliable signal from /cart.js; fall back
          // to the discounts array, then to comparing final vs original price.
          const isGiftLineUndiscounted = (line) => {
            if (typeof line.total_discount === 'number') return line.total_discount === 0;
            if (Array.isArray(line.discounts)) return line.discounts.length === 0;
            if (typeof line.final_line_price === 'number' && typeof line.original_line_price === 'number') {
              return line.final_line_price === line.original_line_price;
            }
            return true;
          };

          // Phase 2 — reconcile each gift variant's line population by count.
          for (const [giftVariantId, desiredCount] of desiredByVariant) {
            // Scoping (critical): only ever touch a line that is BOTH a promo
            // gift (`_free-gift`) AND one of our known member-reward gift variants
            // (variant is a key of desiredByVariant by construction). A
            // customer-purchased gift card (gift_card: true, but no `_free-gift`)
            // is never gathered, counted, decremented, or removed here.
            const giftLines = items.filter(
              (item) => item.properties && item.properties['_free-gift'] && item.variant_id === giftVariantId
            );
            const currentCount = giftLines.reduce((sum, line) => sum + line.quantity, 0);
            const dismissKey = `member_reward_dismissed_${giftVariantId}`;

            // Manual-removal detection: the variant still qualifies
            // (desiredCount > 0) but the shopper just removed one of its gift
            // lines — it appears in items_removed and the live count has dropped
            // below desired. Honor that opt-out for the session. `items_removed`
            // may not preserve line properties, so match on variant_id. Skipped
            // when enforced: an enforced gift can't be manually removed (its
            // remove control is hidden), so it is never remembered as dismissed.
            if (!this.enforceGift && desiredCount > 0 && currentCount < desiredCount) {
              const userRemovedGift = itemsRemoved.some((i) => i && i.variant_id === giftVariantId);
              if (userRemovedGift) {
                try {
                  sessionStorage.setItem(dismissKey, '1');
                } catch (e) {
                  // sessionStorage can throw in private mode; safe to ignore
                }
                if (this.debug) console.log('member_reward: customer removed gift', giftVariantId);
              }
            }

            if (desiredCount === 0) {
              // Variant no longer qualifies — clear any dismissal so the next
              // trigger-add cycle starts fresh.
              try {
                sessionStorage.removeItem(dismissKey);
              } catch (e) {}
            }

            const dismissed = this.enforceGift
              ? false
              : (() => {
                  try {
                    return sessionStorage.getItem(dismissKey) === '1';
                  } catch (e) {
                    return false;
                  }
                })();

            if (currentCount > desiredCount) {
              // Excess — remove/decrement down to desiredCount. This ALWAYS runs,
              // even when dismissed or removeOnly: an un-entitled undiscounted
              // extra (or any stale line once desiredCount is 0) is a bug, never
              // a user choice. Remove undiscounted lines first so the entitled
              // line is the one kept. Whole qty-1 lines are removed; a qty-N line
              // is decremented to land exactly on target — handling both the
              // gift-card norm (many qty-1 lines) and a single merged qty-N line.
              let unitsToRemove = currentCount - desiredCount;
              const orderedLines = [...giftLines].sort(
                (a, b) => (isGiftLineUndiscounted(a) ? 0 : 1) - (isGiftLineUndiscounted(b) ? 0 : 1)
              );
              for (const line of orderedLines) {
                if (unitsToRemove <= 0) break;
                if (line.quantity <= unitsToRemove) {
                  if (this.debug) console.log('member_reward: removing gift line', giftVariantId, line.key);
                  const removed = await this.removeItem(line.key, items.indexOf(line), false);
                  if (!removed) break;
                  mutated = true;
                  unitsToRemove -= line.quantity;
                } else {
                  const targetQuantity = line.quantity - unitsToRemove;
                  if (this.debug) console.log('member_reward: trimming gift qty', line.quantity, '->', targetQuantity);
                  const updated = await this.updateItemQty(line.key, targetQuantity, false);
                  if (!updated) break;
                  mutated = true;
                  unitsToRemove = 0;
                }
              }
            } else if (currentCount < desiredCount) {
              // Shortfall — add the difference, unless this is a connect-time
              // self-heal (removeOnly, never adds) or the shopper dismissed this
              // gift for the session (suppresses adds only).
              if (removeOnly) {
                if (this.debug) console.log('member_reward: removeOnly, skipping add', giftVariantId);
              } else if (dismissed) {
                if (this.debug) console.log('member_reward: dismissed, skipping add', giftVariantId);
              } else {
                const addQuantity = desiredCount - currentCount;
                if (this.debug) console.log('member_reward: adding gift', giftVariantId, 'x', addQuantity);
                const result = await this.addItem({ item: giftVariantId, quantity: addQuantity, quiet });
                if (result?.success) mutated = true;
              }
            }
          }
        } finally {
          this._reconciling = false;
          submitButtons.forEach((button) => button.removeAttribute('disabled'));
        }
        return mutated;
      }

      /**
       * Handles discount code GWP (Gift With Purchase) promotion logic.
       * Gets qualifying discount code, adds gift item if qualified, and publishes
       * cart update or error events.
       *
       * @returns {Promise<boolean>} True if gift was added successfully, false otherwise
       */
      async handleDiscountcodeGWP() {
        try {
          const qualifyingDiscount = await this.getQualifyingDiscount();

          if (qualifyingDiscount) {
            const result = await this.addItem({ discount: qualifyingDiscount });
            if (result.success) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'promotion',
                productVariantId: result.variant,
                cartData: {
                  ...result.response?.items,
                  sections: result.response?.sections,
                },
              });
              return true;
            } else {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'promotion',
                productVariantId: result.variant,
                errors: result.response?.errors || result.response?.description,
                message: result.response?.message,
              });
            }
          }
        } catch (e) {
          console.error(e);
        }

        return false;
      }

      /**
       * Fetches current cart state and determines if a qualifying discount code exists.
       * Verifies discount code prefix match, minimum spend requirement, and checks
       * if gift is already in cart.
       *
       * @returns {Promise<string|false>} Qualifying discount code if found, false otherwise
       */
      async getQualifyingDiscount() {
        try {
          const cart = await fetch('/cart.js');
          const { items, items_subtotal_price, discount_codes } = await cart.json();
          const [discountAllocation] = discount_codes.filter(
            (discount) => discount.applicable && discount.code.includes(this.qualifyingCode)
          );

          // if cart qualifies and doesnt have a gift already
          if (
            discountAllocation?.code &&
            this.minSpend &&
            items_subtotal_price / 100 >= this.minSpend &&
            !items.some((item) => item.properties['_free-gift'])
          ) {
            return discountAllocation.code;
          }
        } catch (e) {
          console.error(e);
        }

        if (this.debug) console.log('no qualifying discount');
        return false;
      }

      /**
       * Main event handler for cart updates.
       * Determines if cart qualifies for gift, adds gift if qualified and not present,
       * removes gift if not qualified or quantity > 1, and handles removed gift edge cases.
       *
       * @param {Object} event - Cart update event from PUB_SUB_EVENTS.cartUpdate
       * @param {Object} event.cartData - Cart data object containing items, prices, discount codes
       */
      handleCartUpdate(event) {
        if (this.debug) console.log('cart update', event);
        const { cartData } = event;
        if (!cartData || !this.promotion.length) return;

        if (this.type === 'bxgy') {
          this.reconcileBXGY(cartData);
          return;
        }

        if (this.type === 'member_reward') {
          // Serialized so this cartUpdate reconcile and the product-form add hook
          // (which fire together — publish is synchronous) can't both add the gift.
          // Pass the event's items_removed through: the queued run reads a fresh
          // GET /cart.js (no items_removed) to close the duplicate window, but the
          // manual-removal / dismissal logic still needs the removal signal.
          this.queueMemberRewardReconcile(false, cartData.items_removed);
          return;
        }

        const { items, items_removed, items_subtotal_price, total_price, discount_codes } = cartData;
        const giftItemIndex = items?.findIndex((item) => item.properties['_free-gift']);
        let qualifiesForGift = false;

        if (!discount_codes) {
          return;
        }
        const [discountAllocation] = discount_codes.filter(
          (discount) => discount.applicable && discount.code.includes(this.qualifyingCode)
        );

        if (this.type === 'discountcode_gwp') {
          qualifiesForGift = items_subtotal_price / 100 >= this.minSpend && discountAllocation?.code;
        } else if (this.type === 'min_spend') {
          qualifiesForGift = total_price / 100 >= this.minSpend;
        }

        try {
          if (this.type === 'discountcode_gwp' && qualifiesForGift && giftItemIndex < 0) {
            // if cart qualifies and doesn't have a gift already, add it
            const removedGift = items_removed.find(
              (item) =>
                this.promotion.filter((promoItem) => promoItem.z / (promoItem.w + 2) === item.product_id).length > 0
            );
            if (!removedGift) {
              if (this.debug) console.log('cart update adding gift');
              this.addItem({ discount: discountAllocation.code });
            }
          } else if (this.type === 'min_spend' && qualifiesForGift && giftItemIndex < 0) {
            // if cart qualifies and doesn't have a gift already, add it
            const removedGift = items_removed.find(
              (item) => this.promotion.filter((promoItem) => promoItem.z === item.product_id).length > 0
            );
            if (!removedGift) {
              if (this.debug) console.log('cart update adding gift');
              this.addItem({ item: this.promotion[0].x });
            }
          } else if (giftItemIndex >= 0) {
            // if cart doesn't qualify or has too many gifts, remove it
            if (!qualifiesForGift || items[giftItemIndex].quantity > 1) {
              if (this.debug) console.log('cart update removing gift');
              this.removeItem(items[giftItemIndex].key, giftItemIndex);
            }
          }
        } catch (e) {
          console.error(e);
        }
      }

      /**
       * Adds promotion gift to cart.
       * Selects variant based on discount code (GWP) or direct ID (min spend),
       * decodes variant ID for discount code GWP, adds '_free-gift' property,
       * and updates cart drawer/page UI.
       *
       * @param {Object} options - Options object
       * @param {string|null} options.discount - Discount code for GWP promotion (optional)
       * @param {number|null} options.item - Direct variant ID for min spend promotion (optional)
       * @param {boolean} [options.quiet] - When true, render the cart without
       *   opening the drawer/notification or moving focus (default false)
       * @returns {Promise<Object>} Result object with success status, response, and variant ID
       * @returns {boolean} returns.success - True if gift was added successfully
       * @returns {Object} returns.response - Cart response from Shopify API (if successful)
       * @returns {number} returns.variant - Variant ID that was added (if successful)
       */
      async addItem({ discount = null, item = null, quantity = 1, quiet = false }) {
        if (this.debug) console.log('adding gift to cart', { quantity });
        let promoVariant = null;

        if (item) {
          promoVariant = item;
        } else if (discount) {
          const hash = this.getCodeHash(discount);
          let category = this.getCategoryFromHash(hash);
          let promoItem = this.promotion.find((promoItem) => promoItem.w === category);

          if (!promoItem || !promoItem.y) {
            promoItem = this.promotion.find((promoItem) => promoItem.y);

            if (!promoItem) {
              return { success: false };
            }
          }

          promoVariant = promoItem.x / (promoItem.w + 2);
        } else {
          return { success: false };
        }

        const formData = {};
        formData.items = [
          {
            id: promoVariant,
            quantity: quantity,
            properties: {
              '_free-gift': true,
            },
          },
        ];

        if (this.cartDrawer) {
          formData.sections = this.cartDrawer.getSectionsToRender().map((section) => section.id);
          formData.sections_url = window.location.pathname;
          this.cartDrawer.setActiveElement(document.activeElement);
        }

        if (this.cartPage) {
          formData.sections.push(...this.cartPage.getSectionsToRender().map((section) => section.section));
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(formData);

        try {
          const data = await fetch(window.Shopify.routes.root + 'cart/add.js', config);
          const response = await data.json();

          if (!response.status) {
            if (this.debug) console.log('gift added successfully', response);
            if (this.cartPage) {
              this.cartPage.renderContents(response);
            } else if (this.cartDrawer) {
              this.cartDrawer.renderContents(response, this.byPassSliderCart, quiet);
            }
            return { success: true, response: response, variant: promoVariant };
          }
        } catch (e) {
          console.error(e);
        }
        return { success: false };
      }

      /**
       * Removes promotion gift from cart.
       * Disables submit buttons during removal, updates cart via Shopify API,
       * re-enables submit buttons after completion, and updates cart drawer/page UI.
       *
       * Mirrors updateItemQty: awaits the POST and re-render and returns a boolean,
       * so a caller that does `await removeItem(...)` only continues once the removal
       * has actually persisted and the cart has re-rendered. This preserves the
       * serialized member-reward reconcile chain — a prematurely-resolving removal
       * would let `_reconciling` clear and the chain advance while the removal POST
       * is still in flight, which is the race that left orphaned gift lines behind.
       *
       * @param {string} itemId - Cart item key/ID to remove
       * @param {number} line - Line item index (for error reporting)
       * @param {boolean} [manageSubmitButtons] - When true (default), disable the
       *   checkout submit buttons for the duration of this call and re-enable them
       *   after. Pass false when a caller (e.g. reconcileMemberReward) already holds
       *   the buttons disabled across a multi-mutation pass, so they aren't toggled
       *   per line.
       * @returns {Promise<boolean>} True if the line was removed successfully
       */
      async removeItem(itemId, line, manageSubmitButtons = true) {
        if (this.debug) console.log('remove item', itemId);

        // disable all submit buttons (unless the caller manages them across a pass)
        const submitBtns = manageSubmitButtons
          ? document.querySelectorAll('.cart__ctas button[type="submit"]')
          : [];
        submitBtns.forEach((btn) => {
          btn.setAttribute('disabled', true);
        });

        const updates = {};
        updates[itemId] = 0;
        const sectionsToRender = this.cartDrawer
          ? this.cartDrawer.getSectionsToRender().map((section) => section.id)
          : [];

        if (this.cartPage) {
          sectionsToRender.push(...this.cartPage.getSectionsToRender().map((section) => section.section));
        }

        const body = JSON.stringify({
          updates: updates,
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(`${routes.cart_update_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: body,
          });
          const parsedState = await response.json();

          if (parsedState.errors) {
            if (this.debug) console.log('errors', parsedState.errors);
            this.updateLiveRegions(line, parsedState.errors);
            return false;
          }

          if (this.cartPage) {
            this.cartPage.renderContents(parsedState);
          } else if (this.cartDrawer) {
            this.cartDrawer.renderContents(parsedState, false, true);
          }
          return true;
        } catch (error) {
          console.error('Error updating cart:', error);
          return false;
        } finally {
          submitBtns.forEach((btn) => {
            btn.removeAttribute('disabled');
          });
        }
      }

      /**
       * Updates an existing cart line's quantity to a specific positive value.
       * Mirrors removeItem's flow but issues a non-zero quantity update so the
       * BXGY gift line can scale up/down as trigger quantities change.
       *
       * @param {string} itemKey - Cart item key/ID to update
       * @param {number} qty - New quantity (positive integer)
       * @param {boolean} [manageSubmitButtons] - When true (default), disable the
       *   checkout submit buttons for the duration of this call and re-enable them
       *   after. Pass false when a caller (e.g. reconcileMemberReward) already holds
       *   the buttons disabled across a multi-mutation pass.
       * @returns {Promise<boolean>} True if cart was updated successfully
       */
      async updateItemQty(itemKey, qty, manageSubmitButtons = true) {
        if (this.debug) console.log('update item qty', itemKey, qty);

        const submitBtns = manageSubmitButtons
          ? document.querySelectorAll('.cart__ctas button[type="submit"]')
          : [];
        submitBtns.forEach((btn) => {
          btn.setAttribute('disabled', true);
        });

        const updates = {};
        updates[itemKey] = qty;
        const sectionsToRender = this.cartDrawer
          ? this.cartDrawer.getSectionsToRender().map((section) => section.id)
          : [];

        if (this.cartPage) {
          sectionsToRender.push(...this.cartPage.getSectionsToRender().map((section) => section.section));
        }

        const body = JSON.stringify({
          updates: updates,
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(`${routes.cart_update_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: body,
          });
          const parsedState = await response.json();

          if (parsedState.errors) {
            if (this.debug) console.log('updateItemQty errors', parsedState.errors);
            return false;
          }

          if (this.cartPage) {
            this.cartPage.renderContents(parsedState);
          } else if (this.cartDrawer) {
            this.cartDrawer.renderContents(parsedState, false, true);
          }
          return true;
        } catch (error) {
          console.error('Error updating cart line:', error);
          return false;
        } finally {
          submitBtns.forEach((btn) => {
            btn.removeAttribute('disabled');
          });
        }
      }

      /**
       * Maps hash value to gift category (0-4).
       * Initial config mode uses predefined character sets, standard mode uses numeric ranges.
       *
       * @param {string|number} hash - Hash value from discount code (string for initial config, number for standard)
       * @returns {number} Category index (0-4)
       */
      getCategoryFromHash(hash) {
        if (this.initialConfig) {
          const lookup = [
            new Set(['T', 'J', '4', 'C', '3', 'S']),
            new Set(['Y', 'A', 'D', '5', 'R', 'Z', 'N']),
            new Set(['F', 'W', 'V', 'Q', 'G', 'L', 'B']),
            new Set(['9', 'P', 'H', 'E', '7', '2']),
            new Set(['U', '6', 'M', 'X', '`K', '8']),
          ];
          return lookup.findIndex((set) => set.has(hash));
        } else {
          if (hash < 3) return 0;
          if (hash < 6) return 1;
          if (hash < 10) return 2;
          if (hash < 50) return 3;
          return 4;
        }
      }

      /**
       * Calculates hash from discount code.
       * Initial config mode returns 5th character directly.
       * Standard mode hashes last 3 characters using CHARSET.
       *
       * @param {string} code - Discount code string
       * @returns {string|number} Hash value (string for initial config, number for standard mode)
       */
      getCodeHash(code) {
        const last3 = code.slice(-3);
        const fifth = code[4];
        let hash = 0;

        if (this.initialConfig) {
          return fifth;
        } else {
          for (let i = 0; i < last3.length; i++) {
            const char = last3[i];
            const charIndex = this.CHARSET.indexOf(char);
            if (charIndex !== -1) {
              hash = (hash * 32 + charIndex) % 100;
            }
          }
        }
        return hash;
      }

      /**
       * Utility to extract HTML from section response.
       * Parses HTML string and queries selector to get innerHTML.
       *
       * @param {string} html - HTML string to parse
       * @param {string} selector - CSS selector to query
       * @returns {string} InnerHTML of the selected element
       */
      getSectionInnerHTML(html, selector) {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }
    }
  );
}
